export type BillerClaimStatusBadgeVariant =
  | "unpaid"
  | "paid"
  | "partially-paid"
  | "denial"
  | "claim-prep"
  | "case-review"
  | "submitted"
  | "draft"
  | "redacted"
  | "accepted"
  | "notice"
  | "default";

/** CSS modifier for biller claim status badges. */
export function getBillerClaimStatusBadgeClass(status: string): string {
  const variant = resolveBillerClaimStatusBadgeVariant(status);
  return `biller-claim-status-badge biller-claim-status-badge--${variant}`;
}

export interface BillerMetricClaimStatusOption {
  metricLabel: string;
  apiValue: string;
  displayLabel: string;
}

export interface BillerClaimStatusSelectOption extends BillerMetricClaimStatusOption {
  disabled: boolean;
}

/** All claim statuses represented on biller metric cards. */
export const BILLER_METRIC_CLAIM_STATUS_OPTIONS: ReadonlyArray<BillerMetricClaimStatusOption> =
  [
    { metricLabel: "Sent", apiValue: "draft", displayLabel: "Sent" },
    { metricLabel: "Redacted", apiValue: "redacted", displayLabel: "Redacted" },
    {
      metricLabel: "Claim Prep",
      apiValue: "claim_prep",
      displayLabel: "Claim Prep",
    },
    {
      metricLabel: "Submitted",
      apiValue: "submitted",
      displayLabel: "Submitted",
    },
    { metricLabel: "Denials", apiValue: "denial", displayLabel: "Denial" },
    { metricLabel: "Unpaid", apiValue: "unpaid", displayLabel: "Unpaid" },
    {
      metricLabel: "Accepted",
      apiValue: "accepted",
      displayLabel: "Accepted",
    },
    {
      metricLabel: "Partially Paid",
      apiValue: "partially_paid",
      displayLabel: "Partially Paid",
    },
    { metricLabel: "Paid", apiValue: "paid", displayLabel: "Paid" },
  ];

/** Forward pipeline rank for biller claim status transition rules. */
export const BILLER_CLAIM_STATUS_PIPELINE_ORDER: readonly string[] = [
  "draft",
  "redacted",
  "claim_prep",
  "submitted",
  "denial",
  "unpaid",
  "accepted",
  "partially_paid",
  "paid",
] as const;

const BILLER_EARLY_CLAIM_STATUS_MAX_RANK = 2;
const BILLER_SUBMITTED_CLAIM_STATUS_MIN_RANK = 3;
const BILLER_SUBMITTED_CLAIM_STATUS_MAX_RANK = 5;
const BILLER_PAYMENT_CLAIM_STATUS_MIN_RANK = 6;

export function getBillerClaimStatusRank(status: string): number | null {
  const option = resolveBillerClaimStatusOption(status);
  if (!option) {
    return null;
  }
  const rank = BILLER_CLAIM_STATUS_PIPELINE_ORDER.indexOf(option.apiValue);
  return rank >= 0 ? rank : null;
}

/**
 * Biller claim status transitions:
 * - Submitted / Denial / Unpaid cannot move back to Sent, Redacted, or Claim Prep.
 * - Accepted / Partially Paid / Paid cannot move to any earlier status.
 */
export function isBillerClaimStatusTransitionAllowed(
  currentStatus: string,
  targetStatus: string
): boolean {
  if (isSameBillerClaimStatus(currentStatus, targetStatus)) {
    return false;
  }

  const currentRank = getBillerClaimStatusRank(currentStatus);
  const targetRank = getBillerClaimStatusRank(targetStatus);
  if (currentRank === null || targetRank === null) {
    return true;
  }

  if (
    currentRank >= BILLER_SUBMITTED_CLAIM_STATUS_MIN_RANK &&
    currentRank <= BILLER_SUBMITTED_CLAIM_STATUS_MAX_RANK &&
    targetRank <= BILLER_EARLY_CLAIM_STATUS_MAX_RANK
  ) {
    return false;
  }

  if (
    currentRank >= BILLER_PAYMENT_CLAIM_STATUS_MIN_RANK &&
    targetRank < currentRank
  ) {
    return false;
  }

  return true;
}

export function getBillerClaimStatusSelectOptions(
  currentStatus: string
): BillerClaimStatusSelectOption[] {
  return BILLER_METRIC_CLAIM_STATUS_OPTIONS.filter(
    (option) => !isSameBillerClaimStatus(currentStatus, option.displayLabel)
  ).map((option) => ({
    ...option,
    disabled: !isBillerClaimStatusTransitionAllowed(
      currentStatus,
      option.displayLabel
    ),
  }));
}

export function getBillerClaimStatusSelectOptionsForMany(
  currentStatuses: readonly string[]
): BillerClaimStatusSelectOption[] {
  if (!currentStatuses.length) {
    return BILLER_METRIC_CLAIM_STATUS_OPTIONS.map((option) => ({
      ...option,
      disabled: false,
    }));
  }

  return BILLER_METRIC_CLAIM_STATUS_OPTIONS.map((option) => ({
    ...option,
    disabled: !currentStatuses.every((currentStatus) =>
      isBillerClaimStatusTransitionAllowed(currentStatus, option.displayLabel)
    ),
  }));
}

/** @deprecated Use getBillerClaimStatusSelectOptions and filter by !disabled. */
export function getAllowedBillerClaimStatusOptions(
  currentStatus: string
): BillerMetricClaimStatusOption[] {
  return getBillerClaimStatusSelectOptions(currentStatus).filter(
    (option) => !option.disabled
  );
}

/** @deprecated Use getBillerClaimStatusSelectOptionsForMany and filter by !disabled. */
export function getAllowedBillerClaimStatusOptionsForMany(
  currentStatuses: readonly string[]
): BillerMetricClaimStatusOption[] {
  return getBillerClaimStatusSelectOptionsForMany(currentStatuses).filter(
    (option) => !option.disabled
  );
}

export function resolveBillerClaimStatusOption(
  status: string
): BillerMetricClaimStatusOption | null {
  const normalized = normalizeBillerClaimStatusKey(status);
  if (!normalized) {
    return null;
  }

  return (
    BILLER_METRIC_CLAIM_STATUS_OPTIONS.find((option) => {
      const candidates = [
        option.apiValue,
        option.displayLabel,
        option.metricLabel,
      ].map(normalizeBillerClaimStatusKey);
      return candidates.includes(normalized);
    }) ?? null
  );
}

export function isRedactedBillerClaimStatusApiValue(apiValue: string): boolean {
  return apiValue.trim().toLowerCase() === "redacted";
}

export function isSameBillerClaimStatus(a: string, b: string): boolean {
  const optionA = resolveBillerClaimStatusOption(a);
  const optionB = resolveBillerClaimStatusOption(b);
  if (optionA && optionB) {
    return optionA.apiValue === optionB.apiValue;
  }
  return normalizeBillerClaimStatusKey(a) === normalizeBillerClaimStatusKey(b);
}

export function normalizeBillerClaimStatusDisplay(status: string): string {
  const resolved = resolveBillerClaimStatusOption(status)?.displayLabel;
  if (resolved) {
    return resolved;
  }
  return capitalizeBillerClaimStatusText(status) || "Sent";
}

function capitalizeBillerClaimStatusText(status: string): string {
  const text = (status ?? "").trim().replace(/_/g, " ");
  if (!text) {
    return "";
  }
  return text
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function normalizeBillerClaimStatusKey(status: string): string {
  const normalized = (status ?? "")
    .trim()
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ");

  if (!normalized) {
    return "";
  }
  if (normalized.includes("denial")) {
    return "denial";
  }
  if (normalized.includes("partial")) {
    return "partially paid";
  }
  if (normalized.includes("accepted")) {
    return "accepted";
  }
  if (normalized.includes("claim prep")) {
    return "claim prep";
  }
  if (normalized === "sent" || normalized === "draft") {
    return "draft";
  }
  return normalized;
}

export function resolveBillerClaimStatusBadgeVariant(
  status: string
): BillerClaimStatusBadgeVariant {
  const normalized = (status ?? "").trim().toLowerCase();
  if (!normalized) {
    return "default";
  }
  if (normalized.includes("partial")) {
    return "partially-paid";
  }
  if (normalized.includes("accepted")) {
    return "accepted";
  }
  if (normalized.includes("unpaid")) {
    return "unpaid";
  }
  if (normalized === "paid" || normalized.includes("paid")) {
    return "paid";
  }
  if (normalized.includes("denial")) {
    return "denial";
  }
  if (normalized.includes("claim prep")) {
    return "claim-prep";
  }
  if (normalized.includes("case review")) {
    return "case-review";
  }
  if (normalized.includes("submitted")) {
    return "submitted";
  }
  if (normalized === "draft" || normalized === "sent") {
    return "draft";
  }
  if (normalized.includes("redacted")) {
    return "redacted";
  }
  if (normalized.includes("notice")) {
    return "notice";
  }
  return "default";
}

/** Map biller metric segment / filter labels to list-biller `claimStatus` values. */
export function billerMetricLabelToClaimStatus(label: string): string | null {
  switch ((label ?? "").trim().toLowerCase()) {
    case "draft":
    case "sent":
      return "draft";
    case "redacted":
      return "redacted";
    case "claim prep":
      return "claim_prep";
    case "submitted":
      return "submitted";
    case "denials":
      return "denial";
    case "unpaid":
      return "unpaid";
    case "paid":
      return "paid";
    case "partially paid":
      return "partially_paid";
    case "accepted":
      return "accepted";
    default:
      return null;
  }
}

export function billerFilterStatusToClaimStatus(
  status: string
): string | null {
  const key = (status ?? "").trim().toLowerCase();
  if (!key) {
    return null;
  }

  const fromLabel = billerMetricLabelToClaimStatus(status);
  if (fromLabel) {
    return fromLabel;
  }

  return (
    BILLER_METRIC_CLAIM_STATUS_OPTIONS.find((option) => option.apiValue === key)
      ?.apiValue ?? null
  );
}

/** Map list-biller claim status API values to sidebar filter radio values. */
export function billerApiStatusToFilterStatus(
  apiStatus: string
): string {
  const key = (apiStatus ?? "").trim().toLowerCase();
  if (!key) {
    return "draft";
  }
  const match = BILLER_METRIC_CLAIM_STATUS_OPTIONS.find(
    (option) => option.apiValue === key
  );
  return match?.apiValue ?? key;
}
